export class ApiError extends Error {
  status: number;
  details?: unknown;
  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.details = details;
  }
}

async function request<T>(path: string, auth: string | null, init: { method?: string; body?: unknown; signal?: AbortSignal } = {}): Promise<T> {
  const res = await fetch(path, {
    method: init.method ?? (init.body !== undefined ? 'POST' : 'GET'),
    headers: { ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}), ...(auth ? { authorization: auth } : {}) },
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    signal: init.signal,
  });
  const text = await res.text();
  const data = text && res.headers.get('content-type')?.includes('json') ? JSON.parse(text) : text;
  if (!res.ok) throw new ApiError(res.status, (data as { error?: string })?.error ?? res.statusText, (data as { details?: unknown })?.details);
  return data as T;
}

// --- staff session -------------------------------------------------------------------------

export interface StaffUser {
  id: string;
  name: string;
  role: 'program' | 'district' | 'facility' | 'clinician' | 'support';
  facility: string | null;
  district: string | null;
}

const TOKEN_KEY = 'movox.staff.token';

export const staff = {
  token: (): string | null => sessionStorage.getItem(TOKEN_KEY),
  async login(username: string, password: string): Promise<StaffUser> {
    const r = await request<{ token: string; user: StaffUser }>('/api/auth/login', null, { body: { username, password } });
    sessionStorage.setItem(TOKEN_KEY, r.token);
    return r.user;
  },
  logout() {
    sessionStorage.removeItem(TOKEN_KEY);
  },
  me: () => staff.get<StaffUser>('/api/auth/me'),
  get<T>(path: string) {
    return request<T>(path, `Bearer ${staff.token()}`);
  },
  post<T>(path: string, body: unknown = {}) {
    return request<T>(path, `Bearer ${staff.token()}`, { body });
  },
};

// --- kiosk device --------------------------------------------------------------------------

export interface KioskCredentials {
  kioskId: string;
  key: string;
}

const KIOSK_KEY = 'movox.kiosk.credentials';

export const kioskCreds = {
  load(): KioskCredentials | null {
    const raw = localStorage.getItem(KIOSK_KEY);
    return raw ? (JSON.parse(raw) as KioskCredentials) : null;
  },
  save(c: KioskCredentials) {
    localStorage.setItem(KIOSK_KEY, JSON.stringify(c));
  },
  clear() {
    localStorage.removeItem(KIOSK_KEY);
  },
};

export function kioskApi(c: KioskCredentials) {
  const auth = `Kiosk ${c.kioskId}:${c.key}`;
  return {
    get: <T>(path: string, signal?: AbortSignal) => request<T>(path, auth, { signal }),
    post: <T>(path: string, body: unknown, signal?: AbortSignal) => request<T>(path, auth, { body, signal }),
  };
}

export function wsUrl(params: Record<string, string>): string {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}/ws?${new URLSearchParams(params)}`;
}
